// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Response Rate Trends Component
// Daily response rate trends by methodology
// ═══════════════════════════════════════════════════════════════════════════════

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useMemo } from 'react';
import { formatUiDate, formatUiPercent } from '@/lib/ui-format';

const formatRate = (value: number): string =>
  formatUiPercent(value / 100, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ResponseRateTrendsProps {
  data?: Array<{
    date: string;
    sent: number;
    replies: number;
    responseRate: number;
    byMethodology: Record<string, { sent: number; replies: number }>;
  }>;
}

interface ChartDataPoint {
  date: string;
  dateFormatted: string;
  sent: number;
  replies: number;
  responseRate: number;
  sandlerRate: number | null;
  challengerRate: number | null;
  spinRate: number | null;
  movingAverage: number | null;
}

interface ResponseRateTooltipPayloadItem {
  name?: string;
  value?: number | null;
  dataKey?: string;
}

interface ResponseRateTooltipProps {
  active?: boolean;
  payload?: Array<ResponseRateTooltipPayloadItem & { payload?: ChartDataPoint }>;
  label?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const METHODOLOGY_COLORS = {
  sandler: 'var(--color-status-info)',
  challenger: 'var(--color-status-danger)',
  spin: 'var(--color-status-success)',
  average: 'var(--color-status-warning)',
  movingAverage: 'var(--color-brand-500)',
} as const;

const METHODOLOGY_LABELS = {
  sandler: 'Sandler',
  challenger: 'Challenger',
  spin: 'SPIN',
  average: 'Overall Average',
  movingAverage: 'Moving Avg (3d)',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calculate response rate from sent and replies
 */
const calculateResponseRate = (sent: number, replies: number): number => {
  if (sent === 0) return 0;
  return Math.round((replies / sent) * 100 * 100) / 100; // Round to 2 decimals
};

/**
 * Calculate 3-day moving average
 */
const calculateMovingAverage = (
  data: ChartDataPoint[],
  index: number,
  windowSize: number = 3
): number | null => {
  if (index < windowSize - 1) return null;

  const window = data.slice(index - windowSize + 1, index + 1);
  const sum = window.reduce((acc, point) => acc + point.responseRate, 0);
  return Math.round((sum / windowSize) * 100) / 100;
};

// ─────────────────────────────────────────────────────────────────────────────
// Tooltip Component
// ─────────────────────────────────────────────────────────────────────────────

const ResponseRateTooltip = ({
  active,
  payload: tooltipPayload,
  label,
}: ResponseRateTooltipProps): JSX.Element | null => {
  if (!active || !tooltipPayload || tooltipPayload.length === 0) {
    return null;
  }

  // Access the full data point from the first payload item
  const dataPoint = tooltipPayload[0]?.payload as ChartDataPoint | undefined;
  const sent = dataPoint?.sent;
  const replies = dataPoint?.replies;

  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 shadow-elevation-2">
      <p className="text-xs font-semibold text-foreground mb-2">{label}</p>
      
      {sent !== undefined && replies !== undefined && (
        <div className="mb-2 space-y-1 text-xs text-muted-foreground border-b border-border pb-2">
          <div className="flex items-center justify-between gap-3">
            <span>Sent</span>
            <span className="text-foreground font-medium">{sent}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Replies</span>
            <span className="text-foreground font-medium">{replies}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Response Rate</span>
            <span className="text-foreground font-medium">
              {formatRate(calculateResponseRate(sent, replies))}
            </span>
          </div>
        </div>
      )}

      <div className="space-y-1 text-xs text-muted-foreground">
        {tooltipPayload
          .filter((item: ResponseRateTooltipPayloadItem) => 
            item.dataKey && 
            ['sandlerRate', 'challengerRate', 'spinRate', 'responseRate', 'movingAverage'].includes(item.dataKey)
          )
          .map((item: ResponseRateTooltipPayloadItem) => {
            // Use name from Line component (already set correctly), fallback to dataKey-based label
            const lineLabel = item.name || METHODOLOGY_LABELS[item.dataKey as keyof typeof METHODOLOGY_LABELS] || item.dataKey;
            const value = item.value;
            
            return (
              <div key={item.dataKey} className="flex items-center justify-between gap-3">
                <span>{lineLabel}</span>
                <span className="text-foreground font-medium">
                  {value !== null && value !== undefined ? formatRate(value) : 'N/A'}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function ResponseRateTrends({ data }: ResponseRateTrendsProps): JSX.Element {
  const chartData = useMemo((): ChartDataPoint[] => {
    if (!data || data.length === 0) return [];

    return data.map((item) => {
      const sandler = item.byMethodology?.sandler;
      const challenger = item.byMethodology?.challenger;
      const spin = item.byMethodology?.spin;

      const sandlerRate = sandler
        ? calculateResponseRate(sandler.sent, sandler.replies)
        : null;
      const challengerRate = challenger
        ? calculateResponseRate(challenger.sent, challenger.replies)
        : null;
      const spinRate = spin
        ? calculateResponseRate(spin.sent, spin.replies)
        : null;

      return {
        date: item.date,
        dateFormatted: formatUiDate(item.date, {
          month: 'short',
          day: '2-digit',
          year: undefined,
        }),
        sent: item.sent,
        replies: item.replies,
        responseRate: item.responseRate,
        sandlerRate,
        challengerRate,
        spinRate,
        movingAverage: null, // Will be calculated after all data points are created
      };
    });
  }, [data]);

  // Calculate moving averages after all data points are created
  const chartDataWithMovingAverage = useMemo((): ChartDataPoint[] => {
    return chartData.map((point, index) => ({
      ...point,
      movingAverage: calculateMovingAverage(chartData, index),
    }));
  }, [chartData]);

  // Calculate overall average response rate
  const averageResponseRate = useMemo((): number => {
    if (chartData.length === 0) return 0;
    const sum = chartData.reduce((acc, point) => acc + point.responseRate, 0);
    return Math.round((sum / chartData.length) * 100) / 100;
  }, [chartData]);

  // Determine which methodologies have data
  const hasSandler = chartData.some((point) => point.sandlerRate !== null);
  const hasChallenger = chartData.some((point) => point.challengerRate !== null);
  const hasSpin = chartData.some((point) => point.spinRate !== null);

  if (!data || data.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg">Response Rate Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No response rate data available
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">Response Rate Trends</CardTitle>
        <div className="flex items-center gap-4 mt-2">
          <p className="text-sm text-muted-foreground">
            Average Response Rate: <span className="font-semibold text-foreground">{formatRate(averageResponseRate)}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Last {data.length} days
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={chartDataWithMovingAverage}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="dateFormatted"
              className="text-xs"
              tick={{ fill: 'var(--muted-foreground)' }}
            />
            <YAxis
              domain={[0, 100]}
              className="text-xs"
              tick={{ fill: 'var(--muted-foreground)' }}
              label={{
                value: 'Response Rate (%)',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: 'var(--muted-foreground)' },
              }}
            />
            <Tooltip content={<ResponseRateTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            
            {/* Overall Average Line */}
            <Line
              type="monotone"
              dataKey="responseRate"
              name={METHODOLOGY_LABELS.average}
              stroke={METHODOLOGY_COLORS.average}
              strokeWidth={2}
              dot={{ fill: METHODOLOGY_COLORS.average, r: 4 }}
              activeDot={{ r: 6 }}
            />

            {/* Methodology Lines */}
            {hasSandler && (
              <Line
                type="monotone"
                dataKey="sandlerRate"
                name={METHODOLOGY_LABELS.sandler}
                stroke={METHODOLOGY_COLORS.sandler}
                strokeWidth={2}
                dot={{ fill: METHODOLOGY_COLORS.sandler, r: 4 }}
                activeDot={{ r: 6 }}
                strokeDasharray="5 5"
              />
            )}
            
            {hasChallenger && (
              <Line
                type="monotone"
                dataKey="challengerRate"
                name={METHODOLOGY_LABELS.challenger}
                stroke={METHODOLOGY_COLORS.challenger}
                strokeWidth={2}
                dot={{ fill: METHODOLOGY_COLORS.challenger, r: 4 }}
                activeDot={{ r: 6 }}
                strokeDasharray="5 5"
              />
            )}
            
            {hasSpin && (
              <Line
                type="monotone"
                dataKey="spinRate"
                name={METHODOLOGY_LABELS.spin}
                stroke={METHODOLOGY_COLORS.spin}
                strokeWidth={2}
                dot={{ fill: METHODOLOGY_COLORS.spin, r: 4 }}
                activeDot={{ r: 6 }}
                strokeDasharray="5 5"
              />
            )}

            {/* Moving Average Line */}
            <Line
              type="monotone"
              dataKey="movingAverage"
              name={METHODOLOGY_LABELS.movingAverage}
              stroke={METHODOLOGY_COLORS.movingAverage}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
              strokeDasharray="8 4"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
